import type { UsageStatsResponse } from "@/lib/types";

import { DailyBarChart } from "./DailyBarChart";
import { EntryPointStackedBar } from "./EntryPointStackedBar";
import { formatInt, formatTokens, getModelLabel } from "./formatters";
import { HorizontalBarRow } from "./HorizontalBarRow";

export function StatsOverview({ data }: { data: UsageStatsResponse }) {
  const maxModelTokens =
    data.byModel.length === 0 ? 0 : Math.max(...data.byModel.map((r) => r.totalTokens));

  const maxUserTokens =
    data.byUser.length === 0 ? 0 : Math.max(...data.byUser.map((r) => r.totalTokens));

  const dailyData = data.byDay.map((row) => ({
    day: row.day,
    responses: row.responses,
    value: row.totalTokens,
  }));

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="border-cyber-border bg-cyber-surface/30 rounded-lg border px-3 py-2">
          <div className="text-cyber-muted/70 text-[10px] tracking-wide uppercase">Prompts</div>
          <div className="text-cyber-text mt-1 text-sm">{formatInt(data.totals.responses)}</div>
        </div>
        <div className="border-cyber-border bg-cyber-surface/30 rounded-lg border px-3 py-2">
          <div className="text-cyber-muted/70 text-[10px] tracking-wide uppercase">
            Total tokens
          </div>
          <div className="text-cyber-cyan mt-1 text-sm">
            {formatTokens(data.totals.totalTokens)}
          </div>
        </div>
        <div className="border-cyber-border bg-cyber-surface/30 rounded-lg border px-3 py-2">
          <div className="text-cyber-muted/70 text-[10px] tracking-wide uppercase">
            Avg steps / prompt
          </div>
          <div className="text-cyber-text mt-1 text-sm">{data.totals.avgStepCount}</div>
        </div>
      </div>

      {/* Daily total tokens chart */}
      <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
        <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
          Daily total tokens
        </h3>
        <DailyBarChart data={dailyData} valueLabel="tokens" />
      </section>

      {/* Two-column grid: Model + Source/Person */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* By Model */}
        <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
          <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">By model</h3>
          <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
            {data.byModel.map((row) => (
              <HorizontalBarRow
                key={`${row.providerId}/${row.modelId}`}
                label={getModelLabel(row.providerId, row.modelId, row.modelName)}
                value={row.totalTokens}
                maxValue={maxModelTokens}
                formattedValue={formatTokens(row.totalTokens)}
                subtitle={`${formatInt(row.responses)} responses`}
              />
            ))}
          </div>
        </section>

        {/* Right column: Source + Person */}
        <div className="space-y-3">
          {/* By entry point */}
          <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
            <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">
              By entry point
            </h3>
            <EntryPointStackedBar data={data.bySource} />
          </section>

          {/* By Person */}
          <section className="border-cyber-border bg-cyber-surface/20 rounded-lg border p-3">
            <h3 className="text-cyber-muted/80 mb-3 text-xs tracking-wide uppercase">By person</h3>
            <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
              {data.byUser.map((row) => (
                <HorizontalBarRow
                  key={row.userEmail}
                  label={row.userEmail}
                  value={row.totalTokens}
                  maxValue={maxUserTokens}
                  formattedValue={formatTokens(row.totalTokens)}
                  subtitle={`${formatInt(row.responses)} responses`}
                  color="rgb(var(--cyber-magenta))"
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
